/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  BuilderFormSchemaFactory,
  BuilderUserContext, FormRootSchema,
  FormStateSchema,
  whenUsOrAuRegion,
  whenUsRegion,
  isJobEditable,
  JobDetailsModel,
} from '@gh/builder-shared';
import { BuilderPermission, JobFinanceType } from '@gh/builder-shared-data';
import { FormState, MaskedTypes, whenEditMode } from '@gh/common-shared';
import { FormMode, getControlValue, markControlAsDirty, setControlValue } from '@gh/core-ui';

import { ContactListItem, JobGeneralTabInfo } from '../../../resources/job-details-tab/job-details-tab-model';

@Injectable()
export class JobDetailsGeneralTabFormState extends FormState<JobGeneralTabInfo> {

  constructor(schemaFactory: BuilderFormSchemaFactory,
              private userContext: BuilderUserContext,
              private model: JobDetailsModel) {
    super(schemaFactory);
  }

  setCounty(county: ContactListItem): void {
    setControlValue(this.group, 'county', county);
    markControlAsDirty(this.group, 'county');
  }

  setFinanceCompany(financeCompany: ContactListItem): void {
    setControlValue(this.group, 'financeCompany', financeCompany);
    markControlAsDirty(this.group, 'financeCompany');
  }

  getCounty(): ContactListItem {
    return getControlValue(this.group, 'county');
  }

  getFinanceCompany(): ContactListItem {
    return getControlValue(this.group, 'financeCompany');
  }

  setSalesperson(salesperson: string): void {
    setControlValue(this.group, 'salespersonName', salesperson);
  }

  updateFinanceCompany(): void {
    const financeCompanyType = this.get('financeType');
    const financeCompany = this.get('financeCompany');
    const isVisible = -1 !== [JobFinanceType.Morgage, JobFinanceType.Escrow].indexOf(financeCompanyType.value);

    financeCompany.updateSchema({
      required: isVisible,
      visible: isVisible,
      enabled: whenEditMode() && isJobEditable(this.model.basicInfo),
    });

    if (!isVisible) {
      financeCompany.reset();
    }
  }

  protected buildSchema(_: JobGeneralTabInfo, { root }: FormStateSchema): FormRootSchema {
    const mode = this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)
      ? FormMode.EditMode
      : FormMode.ViewMode;

    return root({
      mode,
      enabled: whenEditMode(),
      optimisticLock: true,
      children: {
        id: void 0,
        siteName: { enabled: isJobEditable(this.model.basicInfo) },
        siteLotNumber: { enabled: isJobEditable(this.model.basicInfo) },
        siteStreet: { enabled: isJobEditable(this.model.basicInfo) },
        siteStreetNumber: { enabled: isJobEditable(this.model.basicInfo) },
        siteSuburb: { enabled: isJobEditable(this.model.basicInfo) },
        sitePostcode: { visible: whenUsOrAuRegion(), type: MaskedTypes.Zip, enabled: isJobEditable(this.model.basicInfo) },
        siteReference: {
          visible: whenUsRegion(),
          enabled: whenUsRegion() && isJobEditable(this.model.basicInfo),
        },
        baseDesignName: { enabled: false },
        housePlanCode: { enabled: false },
        housePlanName: { enabled: isJobEditable(this.model.basicInfo) },
        contractType: { required: true, enabled: isJobEditable(this.model.basicInfo) },
        county: { enabled: isJobEditable(this.model.basicInfo) },
        financeType: { required: true, enabled: isJobEditable(this.model.basicInfo) },
        financeCompany: { required: true, enabled: isJobEditable(this.model.basicInfo) },
        salespersonName: { enabled: false },
        referredBy: { enabled: false },
        referredDetails: { enabled: false },
        colorConsultantName: { enabled: false },
        estimator: { enabled: isJobEditable(this.model.basicInfo) },
        supervisor: { enabled: isJobEditable(this.model.basicInfo) },
        jobAdmin: { enabled: isJobEditable(this.model.basicInfo) },
        warrantyMonths: { required: true, enabled: isJobEditable(this.model.basicInfo) },
        stage: { enabled: isJobEditable(this.model.basicInfo) },
        salesFolderAddedDate: { enabled: false },
        salesFolderId: { enabled: false },
        salesFolderName: { enabled: false },
        salesFolderStatus: { enabled: false },
        memo: void 0,
        version: void 0,
      },
    });
  }
}
